import { displayToast, sendAppsignalError, toRegExp } from "../modules/utils";

export default class Autocomplete {
  constructor(node) {
    this.element = node;

    this.searchInputs = this.element.querySelectorAll(".js-autocomplete-search-field");
    this.addressLineOneInput = this.element.querySelector(".js-autocomplete-address-line-1"); // Street name
    this.addressLineTwoInput = this.element.querySelector(".js-autocomplete-address-line-2"); // Street number
    this.addressLineThreeInput = this.element.querySelector(".js-autocomplete-address-line-3"); // Additional
    this.postalInput = this.element.querySelector(".js-autocomplete-postal-code");
    this.countryInput = this.element.querySelector(".js-autocomplete-country");
    this.region = this.element.querySelector(".js-autocomplete-region-label");
    this.postalCodeError = this.element.querySelector(".js-autocomplete-postal-code-error");
    this.autocompleteToggle = this.element.querySelector(".js-autocomplete-toggle");
    this.autocompleteToggleWrapper = this.element.querySelector(".js-autocomplete-toggle-wrapper");
    this.postalInputWrapper = this.element.querySelector(".js-autocomplete-postal-code-wrapper");
    this.addressLineTwoInputWrapper = this.element.querySelector(".js-autocomplete-address-line-2-wrapper");

    this.url = this.element.dataset.url;
    this.postalCodeRegex = toRegExp(this.element.dataset.initialPostalCodeRegex);
    this.enabled = this.postalCodeRegex != ""; // We only enable the autocomplete if a postal code regex is given.
    this.forcedDisabled = false; // When the user deliberately disables the autocomplete, we don't want to re-enable it.
    this.dataRegionFieldKey = this.element.dataset.regionField;

    this.initialize();
  }

  initialize() {
    window.addEventListener("beyuna-classes-initialized", () => {
      this.regionSelectInstance = window.Beyuna.objects["region-select"].autocomplete;
    });

    window.addEventListener("region-select-fetch-success", ({ detail }) => {
      // We update the postal code regex when the country changes
      this.postalCodeRegex = toRegExp(detail);
      this.enabled = this.postalCodeRegex != "";

      clearTimeout(this.timeout);

      this.searchInputs.forEach((input) => {
        input.classList.remove("is-invalid");
      });

      this.postalInputWrapper.classList.remove("input-validation-success");
      this.addressLineTwoInputWrapper.classList.remove("input-validation-success");

      this.autocompleteToggleWrapper.classList.add("d-none", !this.enabled);
    });

    this.autocompleteToggle.addEventListener("click", () => {
      this.enabled = !this.enabled;
      this.forcedDisabled = true;

      this.searchInputs.forEach((input) => {
        input.classList.remove("is-invalid");
        input.parentElement.classList.remove("input-validation-success");
      });

      this.autocompleteToggleWrapper.classList.add("d-none");
    });

    this.searchInputs.forEach((input) => {
      input.addEventListener("input", () => this.handleAutocomplete.bind(this)());
    });
  }

  handleAutocomplete() {
    if (this.forcedDisabled || !this.enabled) return; // If the user has disabled the autocomplete, we don't want to re-enable it

    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.searchInputs.forEach((input) => {
        input.classList.remove("is-invalid");
        input.parentElement.classList.remove("input-validation-success");
      });

      if (this.postalInput.value === "") return; // If the postal code is empty, we don't need to validate the format

      if (this.validatePostalCodeFormat()) {
        if (this.addressLineTwoInput.value === "") return; // If the street number is empty, we don't need to fetch the address

        this.fetchAddress();
      } else {
        this.postalInput.classList.add("is-invalid");
      }
    }, 1000); // Throttle the input to prevent too many requests
  }

  validatePostalCodeFormat() {
    const regexResult = this.postalCodeRegex.test(this.postalInput.value);

    this.postalCodeError.classList.toggle("d-none", regexResult);
    this.postalInputWrapper.classList.toggle("input-validation-success", regexResult);

    return regexResult;
  }

  fetchAddress() {
    this.postalInputWrapper.classList.add("loading");
    const formData = new FormData();

    formData.append("postal_code", this.postalInput.value);
    formData.append("country", this.countryInput.dataset.valueCode);
    formData.append("street_number", this.addressLineTwoInput.value);
    formData.append("street", this.addressLineOneInput.value);
    formData.append("additional", this.addressLineThreeInput.value);
    formData.append("region", this.region.dataset.fullName);

    fetch(this.url, {
      retries: 3,
      retryDelay: 1000,
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: formData,
    })
      .then((response) => {
        this.response = response;

        if (response.status === 200) {
          return response.json();
        }

        throw new Error("Something went wrong");
      })
      .then((data) => {
        this.fillInAddress(data);
      })
      .catch((e) => {
        displayToast(this.element.dataset.errorMessage);
        sendAppsignalError(e, "Autocomplete", {
          response: this.response,
        });
      })
      .finally(() => {
        this.postalInputWrapper.classList.remove("loading");
        this.autocompleteToggleWrapper.classList.remove("d-none");
      });
  }

  fillInAddress(address) {
    const addressKeys = Object.keys(address);

    if (addressKeys.length === 0) {
      this.addressLineTwoInputWrapper.classList.add("is-invalid");
      displayToast(this.element.dataset.noResultsFoundMessage, "warning");

      return;
    }

    this.addressLineTwoInputWrapper.classList.add("input-validation-success");

    addressKeys.forEach((key) => {
      const input = this.element.querySelector(`[data-field="${key}"]`);

      if (!input) return;

      if (key === this.dataRegionFieldKey) {
        this.regionSelectInstance.setRegion(address[key]);
      } else {
        input.value = address[key];
      }
    });
  }
}
